import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Save, DollarSign, Power, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ConfigUsdtDeposit() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/usdt-deposit");
      setCfg({
        enabled: !!data?.enabled,
        rate_bdt_per_usd: Number(data?.rate_bdt_per_usd ?? 120),
        min_usd: Number(data?.min_usd ?? 20),
        max_usd: Number(data?.max_usd ?? 50000),
        hybrid_approval: !!data?.hybrid_approval,
        instructions: data?.instructions || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    if (!cfg) return;
    if (!cfg.rate_bdt_per_usd || cfg.rate_bdt_per_usd <= 0) {
      toast.error("Rate must be > 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/config/usdt-deposit", cfg);
      setCfg((prev) => ({ ...prev, ...data }));
      toast.success("USDT deposit config saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div data-testid="config-usdt-deposit-page">
      <PageHeader
        title="Agent USDT Deposit (NOWPayments)"
        subtitle="Auto-configured via NOWPayments.io — wallet addresses are generated per-deposit. Set the USD→BDT conversion rate, limits, and approval mode below."
      />

      <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-start gap-3">
        <Zap className="text-emerald-500 mt-0.5" size={18} />
        <div className="text-[13px] text-emerald-900">
          <div className="font-semibold">Auto-credit enabled</div>
          <div className="text-emerald-800/80 text-[12px]">
            When an agent deposits USDT, NOWPayments handles the wallet and on-chain confirmation. The configured webhook automatically credits the agent&apos;s BDT balance at the rate below. No manual approval required (unless Hybrid Mode is enabled).
          </div>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5 max-w-5xl" data-testid="cfg-usdt-form">
        <Section title="Status & Exchange Rate" icon={DollarSign}>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="cfg-usdt-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable USDT Deposits (Agent Panel)</div>
              <div className="text-xs text-gray-500">When ON, agents can deposit USDT from their panel.</div>
            </div>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="1 USDT = ? BDT (exchange rate)">
              <input
                type="number"
                step="0.01"
                min="0"
                value={cfg.rate_bdt_per_usd}
                onChange={(e) => setCfg({ ...cfg, rate_bdt_per_usd: parseFloat(e.target.value) || 0 })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="cfg-usdt-rate"
              />
            </Field>
            <Field label="Minimum (USDT)">
              <input
                type="number"
                step="0.01"
                min="0"
                value={cfg.min_usd}
                onChange={(e) => setCfg({ ...cfg, min_usd: parseFloat(e.target.value) || 0 })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="cfg-usdt-min"
              />
            </Field>
            <Field label="Maximum (USDT)">
              <input
                type="number"
                step="0.01"
                min="0"
                value={cfg.max_usd}
                onChange={(e) => setCfg({ ...cfg, max_usd: parseFloat(e.target.value) || 0 })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="cfg-usdt-max"
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Example: at rate {cfg.rate_bdt_per_usd || 120}, a 100 USDT deposit credits the agent{" "}
            <span className="font-mono text-emerald-600">
              ৳{(100 * (cfg.rate_bdt_per_usd || 0)).toLocaleString()}
            </span>
            .
          </p>
        </Section>

        <Section title="Approval Mode" icon={Power}>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={cfg.hybrid_approval}
              onChange={(e) => setCfg({ ...cfg, hybrid_approval: e.target.checked })}
              data-testid="cfg-usdt-hybrid"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Hybrid Approval Mode</div>
              <div className="text-xs text-gray-500">
                When <b>OFF</b> (default): NOWPayments confirmed → agent balance credited <b>instantly</b> (full auto).
                <br />
                When <b>ON</b>: NOWPayments confirmed → marked as <span className="font-mono text-sky-700">verified_pending</span>, admin must final-approve from &quot;Agent USDT Deposits&quot; page.
              </div>
            </div>
          </label>
        </Section>

        <Section title="Instructions shown to agents" icon={Power}>
          <textarea
            value={cfg.instructions}
            onChange={(e) => setCfg({ ...cfg, instructions: e.target.value })}
            rows={3}
            className="w-full p-3 border border-gray-200 rounded-sm text-sm"
            data-testid="cfg-usdt-instructions"
          />
        </Section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-usdt-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save USDT Config"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-emerald-500" />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
